import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import { Check, X, Pencil, Save, Ban, CheckCircle2, ClipboardList, AlertTriangle } from "lucide-react";

const ConditionalLayout = ({ embedded, children }) => (embedded ? <>{children}</> : <Layout>{children}</Layout>);

const CLASSES = ["Play Group", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const SECTIONS = ["A", "B", "C", "D", "E"];
const clsLabel = (c) => (["LKG", "UKG", "Play Group"].includes(c) ? c : `${c}`);

function todayISO() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}
function sessionForNow() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes() < 12 * 60 + 30 ? "morning" : "afternoon";
}

export default function AdminAttendance({ embedded = false }) {
  const [className, setClassName] = useState("5");
  const [section, setSection] = useState("A");
  const [date, setDate] = useState(todayISO());
  const [session, setSession] = useState(() => sessionForNow());
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [meta, setMeta] = useState({});
  const [items, setItems] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [leaveMap, setLeaveMap] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [coverage, setCoverage] = useState(null);

  useEffect(() => {
    api.get("/admin/attendance/coverage", { params: { date, session } })
      .then(({ data }) => setCoverage(data))
      .catch(() => setCoverage(null));
  }, [date, session, submitted]);

  useEffect(() => {
    api.get("/meta/classes").then(({ data }) => setAssignments(data.assignments || [])).catch(() => {});
  }, []);

  const availableClasses = useMemo(
    () => [...new Set(assignments.map((a) => a.class_name))].sort(
      (a, b) => (CLASSES.indexOf(a) === -1 ? 99 : CLASSES.indexOf(a)) - (CLASSES.indexOf(b) === -1 ? 99 : CLASSES.indexOf(b))
    ),
    [assignments]
  );
  const availableSections = useMemo(
    () => assignments.filter((a) => a.class_name === className).map((a) => a.section).sort(),
    [assignments, className]
  );

  // Keep the class valid once the registry loads.
  useEffect(() => {
    if (availableClasses.length && !availableClasses.includes(className)) setClassName(availableClasses[0]);
    // eslint-disable-next-line
  }, [availableClasses]);
  // Keep the section valid for the selected class.
  useEffect(() => {
    if (availableSections.length && !availableSections.includes(section)) setSection(availableSections[0]);
    // eslint-disable-next-line
  }, [availableSections]);

  const load = async () => {
    setLoading(true);
    setEditing(false);
    try {
      const { data } = await api.get("/attendance", { params: { class_name: className, section, date, session } });
      const sMap = {}, lMap = {};
      (data.items || []).forEach((it) => { sMap[it.student_id] = it.status; if (it.on_leave) lMap[it.student_id] = true; });
      setItems(data.items || []);
      setStatusMap(sMap);
      setLeaveMap(lMap);
      setSubmitted(!!data.submitted);
      setMeta({ submitted_by: data.submitted_by, submitted_at: data.submitted_at, edited_by: data.edited_by, edited_at: data.edited_at });
    } catch (e) {
      toast.error("Could not load attendance");
      setItems([]); setSubmitted(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [className, section, date, session]);

  const counts = useMemo(() => {
    const present = items.filter((i) => statusMap[i.student_id] === "present").length;
    return { present, absent: items.length - present, total: items.length };
  }, [items, statusMap]);

  const setStatus = (id, status) => {
    if (!editing) return;
    setStatusMap((m) => ({ ...m, [id]: status }));
    if (status === "present") setLeaveMap((l) => { const n = { ...l }; delete n[id]; return n; });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = items.map((s) => ({ student_id: s.student_id, status: statusMap[s.student_id] || "absent", on_leave: !!leaveMap[s.student_id] }));
      await api.post("/attendance", { class_name: className, section, date, session, items: payload });
      toast.success("Attendance updated");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConditionalLayout embedded={embedded}>
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="admin-attendance-page">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList className="h-6 w-6 text-sky-500" />
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Attendance</h1>
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">View any day's submitted attendance. Records are read-only until you tap Edit.</p>

      {/* Coverage summary for the selected date + session */}
      {coverage && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 mb-5" data-testid="aa-coverage">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2.5">
              <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${coverage.pending.length === 0 ? "bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300" : "bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300"}`}>
                {coverage.pending.length === 0 ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
              </span>
              <div>
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" data-testid="aa-coverage-count" style={{ fontFamily: "Outfit" }}>
                  {coverage.marked} out of {coverage.total} classes marked attendance
                </div>
                <div className="text-xs text-slate-500 capitalize">{session} · {new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}</div>
              </div>
            </div>
            <div className="h-2 w-32 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: `${coverage.total ? (coverage.marked / coverage.total) * 100 : 0}%` }} />
            </div>
          </div>
          {coverage.pending.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              <div className="text-xs font-medium text-slate-500 mb-1.5">Yet to mark ({coverage.pending.length}):</div>
              <div className="flex flex-wrap gap-1.5" data-testid="aa-coverage-pending">
                {coverage.pending.map((p) => (
                  <button
                    key={`${p.class_name}-${p.section}`}
                    type="button"
                    onClick={() => { setClassName(p.class_name); setSection(p.section); }}
                    className="text-xs px-2 py-1 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100"
                    data-testid={`aa-pending-${p.class_name}-${p.section}`}
                  >
                    {clsLabel(p.class_name)}-{p.section}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="min-w-[120px]">
          <label className="block text-xs font-medium text-slate-500 mb-1">Class</label>
          <Select value={className} onValueChange={setClassName}>
            <SelectTrigger className="h-10 rounded-xl" data-testid="aa-class-select"><SelectValue /></SelectTrigger>
            <SelectContent>{(availableClasses.length ? availableClasses : CLASSES).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="min-w-[110px]">
          <label className="block text-xs font-medium text-slate-500 mb-1">Section</label>
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger className="h-10 rounded-xl" data-testid="aa-section-select"><SelectValue /></SelectTrigger>
            <SelectContent>{(availableSections.length ? availableSections : SECTIONS).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Date</label>
          <DatePicker value={date} onChange={setDate} quickPicks className="h-10 rounded-xl" data-testid="aa-date-input" />
        </div>
        <div className="min-w-[130px]">
          <label className="block text-xs font-medium text-slate-500 mb-1">Session</label>
          <Select value={session} onValueChange={setSession}>
            <SelectTrigger className="h-10 rounded-xl" data-testid="aa-session-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="morning">Morning</SelectItem>
              <SelectItem value="afternoon">Afternoon</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Status bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-sm">
          {submitted ? (
            <span className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 rounded-full px-3 py-1" data-testid="aa-submitted-badge">
              <CheckCircle2 className="h-4 w-4" /> Submitted · {counts.present}/{counts.total} present
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-slate-500 bg-slate-100 dark:bg-slate-800 rounded-full px-3 py-1" data-testid="aa-not-submitted-badge">
              Not submitted for this day/session
            </span>
          )}
        </div>
        {submitted && items.length > 0 && (
          editing ? (
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => load()} data-testid="aa-cancel-btn"><Ban className="h-4 w-4 mr-1" /> Cancel</Button>
              <Button className="bg-sky-500 hover:bg-sky-600 rounded-full" onClick={save} disabled={saving} data-testid="aa-save-btn"><Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save changes"}</Button>
            </div>
          ) : (
            <Button variant="outline" className="rounded-full" onClick={() => setEditing(true)} data-testid="aa-edit-btn"><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
          )
        )}
      </div>

      {(meta.edited_by || meta.submitted_by) && (
        <p className="text-xs text-slate-400 mb-3" data-testid="aa-meta-note">
          {meta.submitted_by ? `Submitted by ${meta.submitted_by}. ` : ""}
          {meta.edited_by ? `Last edited by ${meta.edited_by}${meta.edited_at ? ` on ${new Date(meta.edited_at).toLocaleString()}` : ""}.` : ""}
        </p>
      )}

      {/* Roster */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400" data-testid="aa-loading">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-400" data-testid="aa-empty">No attendance found for this selection.</div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="aa-list">
            {items.map((s) => {
              const present = statusMap[s.student_id] === "present";
              return (
                <li key={s.student_id} className="flex items-center justify-between gap-4 px-4 sm:px-6 py-3" data-testid={`aa-row-${s.roll_number}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 dark:text-slate-100 truncate">{s.name}</div>
                    <div className="text-xs text-slate-400">#{s.roll_number}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {leaveMap[s.student_id] && <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-amber-100 text-amber-700">On leave</span>}
                    <div className="inline-flex rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-1">
                      <button
                        type="button"
                        onClick={() => setStatus(s.student_id, "present")}
                        disabled={!editing}
                        data-testid={`aa-present-${s.roll_number}`}
                        className={`px-3 h-8 rounded-full text-sm font-medium flex items-center gap-1 disabled:cursor-default ${present ? "bg-emerald-500 text-white shadow-sm" : "text-slate-400"}`}
                      ><Check className="h-4 w-4" /> Present</button>
                      <button
                        type="button"
                        onClick={() => setStatus(s.student_id, "absent")}
                        disabled={!editing}
                        data-testid={`aa-absent-${s.roll_number}`}
                        className={`px-3 h-8 rounded-full text-sm font-medium flex items-center gap-1 disabled:cursor-default ${!present ? "bg-rose-500 text-white shadow-sm" : "text-slate-400"}`}
                      ><X className="h-4 w-4" /> Absent</button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
    </ConditionalLayout>
  );
}
